"use client";

import { motion } from "framer-motion";
import { PortableText } from "next-sanity";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { GroqImage } from "@/helpers/Image";
import { PageManifestoSchema } from "@/sanity/pages/PageManifestoTemplate";
import { CTAButton } from "@/shadcn/ui/button";

const ease = [0.25, 0.1, 0.25, 1] as const;

export default function HeroSection({ data }: { data: PageManifestoSchema }) {
  return (
    <section className="relative w-full flex flex-col items-center pt-[7rem] pb-[5.5rem] lg:pt-[11rem] lg:pb-[8rem]">
      {data?.heroBackgroundImage && (
        <div
          className="pointer-events-none absolute w-full h-[130rem] top-0 left-0"
          aria-hidden="true"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.4, ease }}
            className="w-full h-full"
          >
            <GroqImage
              src={data.heroBackgroundImage}
              alt=""
              className="!w-full !h-full !object-cover !object-bottom lg:opacity-35"
            />
          </motion.div>
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, #fff 0%, transparent 40%), linear-gradient(0deg, #fff 20%, transparent 66%)",
            }}
          />
        </div>
      )}
      <div className="relative z-10 lg:w-[min(94.5rem,calc(100%-8rem))] w-[min(94.5rem,calc(100%-4rem))] mx-auto flex flex-col items-center gap-[1.5rem]">
        {data?.heroChip && (
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease, delay: 0.1 }}
            className="mb-0 font-blacker italic text-[2rem] leading-[1.5] text-black"
          >
            {data.heroChip}
          </motion.p>
        )}

        {data?.heroTitle && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease, delay: 0.25 }}
            className="richtext text-center"
          >
            <PortableText
              value={data.heroTitle}
              components={richTextComponent}
            />
          </motion.div>
        )}

        {(data?.heroAuthorPhoto || data?.heroAuthorName) && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease, delay: 0.4 }}
            className="flex items-center gap-[1rem] mt-[0.5rem]"
          >
            {data?.heroAuthorPhoto && (
              <div className="w-[4rem] h-[4rem] rounded-full overflow-hidden shrink-0">
                <GroqImage
                  src={data.heroAuthorPhoto}
                  alt={data.heroAuthorName || "Author"}
                  className="!w-full !h-full !object-cover"
                />
              </div>
            )}
            {data?.heroAuthorName && (
              <p className="mb-0 text-[2rem] text-black/60 font-normal leading-[1.5]">
                {({ de: "Geschrieben von", br: "Escrito por" }[
                  data.settings.language ?? ""
                ] ?? "Written by") + " "}
                <span className="font-semibold text-black">
                  {data.heroAuthorName}
                </span>
              </p>
            )}
          </motion.div>
        )}

        {data?.heroButton && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease, delay: 0.55 }}
            className="w-full lg:w-fit mt-[1.5rem]"
          >
            <CTAButton data={data.heroButton} />
          </motion.div>
        )}
      </div>
    </section>
  );
}
